import { useState } from "react";
import Footer from "../../components/feature/Footer";

const SERVER_IP = "applesmp.us";
const JAVA_PORT = "25565";
const BEDROCK_PORT = "19132";

function CopyButton({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    await navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };

  return (
    <button
      onClick={copy}
      className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.06] px-3.5 py-2 text-sm font-black text-white transition-all hover:border-red-400/30 hover:bg-white/[0.10]"
    >
      <i className={copied ? "ri-checkbox-circle-fill text-emerald-400" : "ri-file-copy-line text-zinc-300"} />
      {copied ? "Copied" : label}
    </button>
  );
}

function Step({ number, title, children }: { number: string; title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-[1.25rem] border border-white/10 bg-[#151515] p-5 shadow-xl shadow-black/20">
      <div className="flex gap-4">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-red-500 text-sm font-black text-white">
          {number}
        </div>
        <div>
          <h3 className="text-lg font-black text-white">{title}</h3>
          <div className="mt-2 text-sm font-medium leading-6 text-white/65">{children}</div>
        </div>
      </div>
    </div>
  );
}

export default function ConnectPage() {
  return (
    <div className="flex min-h-screen flex-col bg-[#090909] text-white selection:bg-red-500/30">
      <main className="relative flex-1 overflow-hidden">
        <div className="pointer-events-none absolute left-1/2 top-0 h-96 w-96 -translate-x-1/2 rounded-full bg-red-500/10 blur-[120px]" />
        <div className="pointer-events-none absolute bottom-20 right-0 h-80 w-80 rounded-full bg-sky-400/10 blur-[120px]" />

        <section className="relative mx-auto w-full max-w-6xl px-5 py-12 md:py-16">
          <div className="grid gap-6 lg:grid-cols-[1.05fr_0.95fr] lg:items-stretch">
            <div className="rounded-[1.5rem] border border-white/10 bg-[#151515] p-6 shadow-2xl shadow-black/25 md:p-8">
              <div className="inline-flex items-center gap-2 rounded-full border border-sky-400/15 bg-sky-400/[0.08] px-3 py-1.5 text-xs font-black uppercase tracking-[0.18em] text-sky-200">
                <i className="ri-gamepad-line text-base" />
                Play on Console
              </div>
              <h1 className="mt-5 text-4xl font-black tracking-tight md:text-6xl">
                Connect to <span className="text-red-400">AppleSMP</span>
              </h1>
              <p className="mt-4 max-w-2xl text-base font-medium leading-7 text-white/65">
                Use Bedrock Together on your phone to make AppleSMP appear as a LAN server on Xbox, PlayStation, or Switch.
              </p>

              <div className="mt-7 grid gap-3 sm:grid-cols-2">
                <div className="rounded-2xl border border-white/10 bg-black/20 p-4">
                  <p className="text-xs font-black uppercase tracking-[0.18em] text-white/40">Server Address</p>
                  <p className="mt-2 font-mono text-2xl font-black text-white">{SERVER_IP}</p>
                  <div className="mt-4">
                    <CopyButton value={SERVER_IP} label="Copy IP" />
                  </div>
                </div>
                <div className="rounded-2xl border border-white/10 bg-black/20 p-4">
                  <p className="text-xs font-black uppercase tracking-[0.18em] text-white/40">Bedrock Port</p>
                  <p className="mt-2 font-mono text-2xl font-black text-white">{BEDROCK_PORT}</p>
                  <div className="mt-4">
                    <CopyButton value={BEDROCK_PORT} label="Copy Port" />
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-[1.5rem] border border-white/10 bg-[#111113] p-6 shadow-2xl shadow-black/25 md:p-8">
              <div className="flex h-full flex-col justify-between gap-6">
                <div>
                  <i className="ri-router-line text-5xl text-sky-300" />
                  <h2 className="mt-5 text-3xl font-black">Bedrock Together Setup</h2>
                  <p className="mt-3 text-sm font-medium leading-6 text-white/60">
                    Install Bedrock Together on a phone connected to the same Wi-Fi as your console. Start the AppleSMP relay, keep the app open, then join from Minecraft's Friends tab.
                  </p>
                </div>
                <div className="rounded-2xl border border-red-400/20 bg-red-500/[0.08] p-4">
                  <p className="text-sm font-bold leading-6 text-red-100">
                    Only enter AppleSMP: server <span className="font-mono text-white">{SERVER_IP}</span>, port <span className="font-mono text-white">{BEDROCK_PORT}</span>.
                  </p>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <a
                    href="https://apps.apple.com/us/app/bedrocktogether/id1534593376"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/[0.06] px-4 py-3 text-sm font-black text-white transition-all hover:border-sky-300/30 hover:bg-white/[0.10]"
                  >
                    <i className="ri-apple-fill" />
                    App Store
                  </a>
                  <a
                    href="https://play.google.com/store/search?q=bedrock%20together&c=apps"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/[0.06] px-4 py-3 text-sm font-black text-white transition-all hover:border-emerald-300/30 hover:bg-white/[0.10]"
                  >
                    <i className="ri-google-play-fill" />
                    Google Play
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 grid gap-4 lg:grid-cols-3">
            <Step number="1" title="Download Bedrock Together">
              Install Bedrock Together on your phone. Make sure your phone and console are connected to the same Wi-Fi network.
            </Step>
            <Step number="2" title="Enter AppleSMP">
              In Bedrock Together, set the server address to <span className="font-mono text-white">{SERVER_IP}</span> and the port to <span className="font-mono text-white">{BEDROCK_PORT}</span>, then start the relay.
            </Step>
            <Step number="3" title="Join from console">
              Open Minecraft on your console, go to Friends, then join the LAN game that appears. Keep Bedrock Together open until you are connected.
            </Step>
          </div>

          <div className="mt-4 rounded-[1.25rem] border border-white/10 bg-[#111113] p-5">
            <h2 className="text-lg font-black">Java and normal Bedrock players</h2>
            <p className="mt-2 text-sm font-medium leading-6 text-white/60">
              Java can join directly with <span className="font-mono text-white">{SERVER_IP}</span>. Bedrock mobile/PC can add the server directly with address <span className="font-mono text-white">{SERVER_IP}</span> and port <span className="font-mono text-white">{BEDROCK_PORT}</span>. Bedrock Together is mainly for consoles that do not show custom servers normally.
            </p>
          </div>

          <div className="mt-6 rounded-[1.35rem] border border-white/10 bg-[#151515] p-5 shadow-xl shadow-black/20">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div>
                <h2 className="text-xl font-black">Still cannot connect?</h2>
                <p className="mt-1 text-sm font-medium text-white/55">
                  Join the Discord and open a Minecraft Support ticket with your device and error message.
                </p>
              </div>
              <a
                href="https://www.dsc.gg/applesmpofficial"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-indigo-500 px-4 py-3 text-sm font-black text-white transition-all hover:bg-indigo-400"
              >
                <i className="ri-discord-fill" />
                Discord Support
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
